import React, { Fragment, useEffect, useState } from 'react'
import injectStyles from 'react-jss'

import StyledBreadcrumbs from '../../../Components/StyledBreadcrumbs'
import { SettingsIcon } from '../../../Assets/Images/icons/SettingsIcon'
import { PATHS } from '../../../Config/Routing/paths'
import { PageTitle } from '../../../Components/PageTitle'
import LoadingIndicator from '../../../Components/LoadingIndicator/LoadingIndicator'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableRow from '@material-ui/core/TableRow'
import Paper from '@material-ui/core/Paper'
import Checkbox from '@material-ui/core/Checkbox'
import SubmitButton from '../../../Components/Form/Button'
import { apiXero } from '../../../Module/API/Xero'
import XeroConnected from '../../../Components/XeroConnected'
import { openLinkInNewTab } from '../../Country/Page/Pipeline/PipelineView/utils'
import EnhancedTableToolbar from './components/EnhancedTableToolbar'
import EnhancedTableHead from './components/EnhancedTableHead'
import {
  formatDate,
  handleSelectAllClick,
  handleSelectClick,
  isSelected,
} from './utils'
import { styles } from './styles'
import { headCellsWithoutPayment, headCellsWithPayment } from './options'
import ConfirmDialog from '../../../Components/ConfirmDialog/ConfirmDialog'
import { handleConfirmationDialog } from '../../City/Page/Ambasadors/EmailPipelineView/utils'

const AddPayment = ({ classes }) => {
  const [isLoading, setIsLoading] = useState(true)

  const [selected, setSelected] = useState([])
  const [tenant, setTenant] = useState(null)
  const [invoicesWithoutPayment, setInvoicesWithoutPayment] = useState([])
  const [invoicesWithPayment, setInvoicesWithPayment] = useState([])
  const [confirmDialogData, setConfirmDialogData] = useState({ isOpen: false })
  const [isClickedToSave, setIsClickedToSave] = useState(false)
  const [isNeedRefetch, setIsNeedRefetch] = useState(false)

  useEffect(() => {
    fetchTenant()
  }, [])

  useEffect(() => {
    if (isNeedRefetch) {
      fetchTenant()
    }
  }, [isNeedRefetch])

  useEffect(() => {
    if (isClickedToSave) {
      prepareToSave()
    }
  }, [selected.length, isClickedToSave])

  const addPayments = async invoices => {
    setIsLoading(true)
    try {
      await apiXero.addPaymentsToAuthorisedInvoices({
        tenantId: tenant.tenantId,
        invoices,
      })
    } catch (e) {
      console.error(e)
    } finally {
      setIsNeedRefetch(true)
    }
  }

  const prepareToSave = async () => {
    const invoicesWithErrors = selected.filter(
      item => !item.userTaxPercentage || !item.futurePayment,
    )

    if (invoicesWithErrors.length) {
      const contentText = (
        <div>
          Something wrong with:
          {invoicesWithErrors.map(item => (
            <div>{item.invoiceNumber}</div>
          ))}
          <div>invoices, cannot calculate payment.</div>
          <div>
            You can skip this invoices and create payment to all valid invoices
          </div>
        </div>
      )

      handleConfirmationDialog({
        title: 'Validation Error',
        contentText,
        setConfirmDialogData,
        hasOptions: true,
        rightButtonLable: 'Skip and Make payment',
        leftButtonLable: 'Cancel',
        onConfirm: async () => {
          const validInvoices = selected.filter(
            item => item.userTaxPercentage && item.futurePayment,
          )
          await addPayments(validInvoices)
        },
      })
    } else {
      await addPayments(selected)
    }

    setIsClickedToSave(false)
  }

  const fetchTenant = async () => {
    setIsLoading(true)
    setInvoicesWithoutPayment([])
    setInvoicesWithPayment([])
    try {
      const result = await apiXero.getXeroTenant()
      if (result && result.isAuthorized && result.isConnected) {
        setTenant(result)

        const invoices = await apiXero.getAuthorisedInvoices({
          tenantId: result.tenantId,
        })

        if (invoices.success) {
          if (invoices.withoutPayment.length) {
            setInvoicesWithoutPayment(invoices.withoutPayment)
          }
          if (invoices.withPayment.length) {
            setInvoicesWithPayment(invoices.withPayment)
          }
        }
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
      setIsNeedRefetch(false)
      setSelected([])
    }
  }

  return (
    <div className={classes.wrapper}>
      {confirmDialogData.isOpen ? (
        <ConfirmDialog {...confirmDialogData} />
      ) : null}
      <StyledBreadcrumbs
        icon={<SettingsIcon />}
        breadcrumbs={[
          {
            label: 'Settings',
            link: `${PATHS.main.dashboard}${PATHS.dashboard.settings}`,
          },
          {
            label: 'Withhold payments in Xero',
          },
        ]}
      />
      <PageTitle className={classes.title}>
        Apply withholding tax to contractors bills
      </PageTitle>
      <div className={classes.header}>
        <div>
          <XeroConnected isConnected={tenant} />
          <div>Entity: {tenant ? tenant.name : ' - '}</div>
        </div>
        <SubmitButton
          value="Go to Xero settings"
          onClick={() => {
            openLinkInNewTab(
              `${PATHS.main.dashboard}${PATHS.dashboard.settings}${
                PATHS.settings.xeroConnection
              }`,
            )
          }}
        />
      </div>
      {isLoading ? (
        <LoadingIndicator style={{ height: '50vh' }} label="Loading" />
      ) : (
        <Fragment>
          <div className={classes.container}>
            <div className={classes.root}>
              <Paper className={classes.paper}>
                <EnhancedTableToolbar
                  numSelected={selected.length}
                  onSave={() => setIsClickedToSave(true)}
                  title="Approved bills to withhold"
                />
                <TableContainer className={classes.tableContainer}>
                  <Table
                    className={classes.table}
                    aria-labelledby="tableTitle"
                    size="small"
                    aria-label="enhanced table"
                    stickyHeader
                  >
                    <EnhancedTableHead
                      styles={styles}
                      numSelected={selected.length}
                      onSelectAllClick={event =>
                        handleSelectAllClick(
                          event,
                          setSelected,
                          invoicesWithoutPayment,
                        )
                      }
                      rowCount={invoicesWithoutPayment.length}
                      headers={headCellsWithoutPayment}
                    />
                    <TableBody>
                      {invoicesWithoutPayment.map((row, index) => {
                        const isItemSelected = isSelected(row.id, selected)
                        const labelId = `enhanced-table-checkbox-${index}`
                        return (
                          <TableRow
                            hover
                            onClick={event =>
                              handleSelectClick(
                                event,
                                row,
                                selected,
                                setSelected,
                              )
                            }
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            key={row.id}
                            selected={isItemSelected}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isItemSelected}
                                inputProps={{ 'aria-labelledby': labelId }}
                              />
                            </TableCell>
                            <TableCell
                              component="th"
                              id={labelId}
                              scope="row"
                              padding="none"
                              align="left"
                            >
                              {row.invoiceNumber}
                            </TableCell>
                            <TableCell align="left">
                              {row.contactName}
                            </TableCell>
                            <TableCell align="center">
                              {formatDate(row.date)}
                            </TableCell>
                            <TableCell align="center">{row.subtotal}</TableCell>
                            <TableCell align="center">{row.tax}</TableCell>
                            <TableCell align="center">{row.total}</TableCell>
                            <TableCell align="center">
                              {row.userTaxPercentage}
                            </TableCell>
                            <TableCell align="center">
                              {row.futurePayment}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              {invoicesWithPayment.length ? (
                <Paper className={classes.paper}>
                  <EnhancedTableToolbar title="Approved bills with payment applied" />
                  <TableContainer className={classes.tableContainer}>
                    <Table
                      className={classes.table}
                      aria-labelledby="tableTitle"
                      size="small"
                      aria-label="enhanced table"
                      stickyHeader
                    >
                      <EnhancedTableHead
                        styles={styles}
                        numSelected={selected.length}
                        onSelectAllClick={event =>
                          handleSelectAllClick(
                            event,
                            setSelected,
                            invoicesWithoutPayment,
                          )
                        }
                        rowCount={invoicesWithoutPayment.length}
                        headers={headCellsWithPayment}
                        withCheckbox={false}
                      />
                      <TableBody>
                        {invoicesWithPayment.map((row, index) => {
                          const labelId = `enhanced-table-${index}`
                          return (
                            <TableRow key={row.id}>
                              <TableCell
                                component="th"
                                id={labelId}
                                scope="row"
                                padding="none"
                                align="center"
                              >
                                {row.invoiceNumber}
                              </TableCell>
                              <TableCell align="center">
                                {row.contactName}
                              </TableCell>
                              <TableCell align="center">
                                {formatDate(row.date)}
                              </TableCell>
                              <TableCell align="center">
                                {row.subtotal}
                              </TableCell>
                              <TableCell align="center">{row.tax}</TableCell>
                              <TableCell align="center">{row.total}</TableCell>
                              <TableCell align="center">
                                {row.addedPayment}
                              </TableCell>
                              <TableCell align="center">
                                {formatDate(row.paymentDate)}
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ) : null}
            </div>
          </div>
        </Fragment>
      )}
    </div>
  )
}

export default injectStyles(styles)(AddPayment)
